// src/utils/deepLinkUtils.js
import { supportedChains } from '../chains';
import { ethers } from 'ethers';

/**
 * Process a deep link based on type and parameters
 * @param {string} linkType - The type of deep link (e.g., 'chain')
 * @param {Array} params - Parameters for the deep link
 * @returns {boolean} Success status
 */
export const processDeepLink = (linkType, params) => {
  if (linkType === 'chain') {
    const [chainKey, tokenAddress] = params;
    
    // Validate chain
    if (!supportedChains[chainKey]) {
      console.error(`Invalid chain key: ${chainKey}`);
      return false;
    }
    
    // Store chain selection in session storage
    sessionStorage.setItem('deep_link_chain', chainKey);
    console.log(`Deep link: Storing chain key ${chainKey} in session storage`);
    
    // If there's a token address, store it for swap
    if (tokenAddress && ethers.isAddress(tokenAddress)) {
      const tokenData = JSON.stringify({
        token: { address: tokenAddress },
        action: 'buy'
      });
      
      sessionStorage.setItem('deep_link_swap_token', tokenData);
      console.log(`Deep link: Storing token address ${tokenAddress} for swap`);
      
      // Set a flag specifically for Telegram to know we need to navigate to swap
      if (isTelegramWebApp()) {
        sessionStorage.setItem('telegram_needs_swap_navigation', 'true');
        console.log('Set Telegram swap navigation flag');
      }
    }
    
    return true;
  }
  
  return false;
};

/**
 * Handle a universal deep link from URL or path
 * @param {string} url - The URL or path to process
 * @returns {boolean} Success status
 */
export const handleUniversalDeepLink = (url) => {
  // If it's a full URL, extract the path
  let path = url;
  if (url.startsWith('http')) {
    try {
      const urlObj = new URL(url);
      path = urlObj.pathname;
    } catch (e) {
      console.error('Invalid URL:', url, e);
      return false;
    }
  }
  
  // Remove leading slash if present
  if (path.startsWith('/')) {
    path = path.substring(1);
  }
  
  // Split the path into segments
  const segments = path.split('/');
  
  // Check if it's a valid deep link pattern
  if (segments.length >= 2 && segments[0] === 'chain') {
    console.log(`Processing web deep link: ${path}`);
    return processDeepLink('chain', segments.slice(1));
  }
  
  return false;
};

/**
 * Handle Telegram specific start parameters
 * @param {string} startParam - The Telegram start parameter
 * @returns {boolean} Success status
 */
export const handleTelegramParams = (startParam) => {
  if (!startParam) return false;
  
  console.log(`Processing Telegram start parameter: ${startParam}`);
  
  const parts = startParam.split('_');
  
  if (parts.length >= 2 && parts[0] === 'chain') {
    return processDeepLink('chain', parts.slice(1));
  }
  
  return false;
};

/**
 * Check if the app is running in Telegram WebApp
 * @returns {boolean} True if running in Telegram WebApp
 */
export const isTelegramWebApp = () => {
  return !!(window.Telegram && window.Telegram.WebApp);
};

/**
 * Initialize Telegram WebApp features
 * @returns {Object|null} The Telegram WebApp object or null if not in Telegram
 */
export const initTelegramWebApp = () => {
  if (isTelegramWebApp()) {
    const webApp = window.Telegram.WebApp;
    
    // Enable closing confirmation if needed
    webApp.enableClosingConfirmation();
    
    // Expand the WebApp to take full screen space
    webApp.expand();
    
    console.log("Telegram WebApp initialized");
    
    return webApp;
  }
  
  return null;
};

/**
 * Generate universal deep links for both web and Telegram
 * @param {string} chainKey - The blockchain network key
 * @param {string|null} tokenAddress - Optional token address for swap
 * @returns {Object} Object containing webUrl and telegramUrl
 */
export const generateUniversalDeepLink = (chainKey, tokenAddress = null) => {
  // Web URL format
  const webUrl = tokenAddress 
    ? `https://bitdogwallet.com/chain/${chainKey}/${tokenAddress}`
    : `https://bitdogwallet.com/chain/${chainKey}`;
    
  // Telegram parameter format
  const telegramParam = tokenAddress
    ? `chain_${chainKey}_${tokenAddress}`
    : `chain_${chainKey}`;
    
  return {
    webUrl,
    telegramUrl: `https://t.me/BitDogWallet_Bot?startapp=${telegramParam}`,
    telegramParam
  };
};